<script setup lang="ts">
import { LMap, LMarker, LPopup, LTileLayer } from '@vue-leaflet/vue-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Initialize Leaflet icons before vue-leaflet components are used
// This fixes the "Cannot read properties of undefined (reading 'Default')" error
if (typeof window !== 'undefined') {
  // Ensure Icon.Default exists before accessing it
  if (L && L.Icon && L.Icon.Default) {
    // Delete the default icon URL getter to prevent errors
    delete (L.Icon.Default.prototype as { _getIconUrl?: string })._getIconUrl

    // Set the correct paths for default icon images (from public folder)
    L.Icon.Default.mergeOptions({
      iconUrl: '/leaflet-icons/marker-icon.png',
      iconRetinaUrl: '/leaflet-icons/marker-icon-2x.png',
      shadowUrl: '/leaflet-icons/marker-shadow.png',
    })
  }
}

const props = withDefaults(defineProps<{
  markers?: { title: string, lat: number, lng: number, description: string }[]
  lat?: number
  lng?: number
  zoom?: number
  openPopupIndex?: number | null
  autoOpenFirst?: boolean
  popupWidth?: number | string
}>(), {
  markers: () => [],
  lat: 46.227638,
  lng: 2.213749,
  zoom: 6,
  openPopupIndex: null,
  autoOpenFirst: true,
  popupWidth: 300,
})

const map = useTemplateRef<typeof LMap>('map')
const markerRefs = ref<(InstanceType<typeof LMarker> | null)[]>([])

// Open popup when map is ready
const openPopup = () => {
  if (!props.markers || props.markers.length === 0) {
    return
  }

  // Use setTimeout to ensure the map and markers are fully initialized
  setTimeout(() => {
    let markerIndex = 0

    // If openPopupIndex is specified, use that; otherwise use first marker if autoOpenFirst is true
    if (props.openPopupIndex !== null && props.openPopupIndex >= 0 && props.openPopupIndex < props.markers.length) {
      markerIndex = props.openPopupIndex
    }
    else if (props.autoOpenFirst) {
      markerIndex = 0
    }
    else {
      return
    }

    const marker = markerRefs.value[markerIndex]
    if (marker?.leafletObject) {
      marker.leafletObject.openPopup()
    }
  }, 100)
}

// Handle map ready event
const onMapReady = () => {
  openPopup()
}

// Watch for map ready and open popup
watch(() => map.value?.ready, (ready) => {
  if (ready) {
    openPopup()
  }
}, { immediate: true })

// Watch for markers or openPopupIndex changes
watch([() => props.markers, () => props.openPopupIndex, () => props.autoOpenFirst], () => {
  if (map.value?.ready) {
    openPopup()
  }
}, { deep: true })
</script>

<template>
  <div class="relative z-0">
    <LMap
      ref="map"
      class="relative w-full min-h-80"
      :zoom="+zoom"
      :center="[+lat, +lng]"
      :use-global-leaflet="false"
      @ready="onMapReady"
    >
      <LTileLayer
        url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
        layer-type="base"
        name="OpenStreetMap"
      />
      <LMarker
        v-for="(marker, index) in markers"
        :key="index"
        :ref="(el) => { if (el) markerRefs[index] = el as InstanceType<typeof LMarker> }"
        :lat-lng="[+marker.lat, +marker.lng]"
      >
        <LPopup :options="{ maxWidth: typeof popupWidth === 'number' ? popupWidth : parseInt(popupWidth) }">
          <h2 class="text-2xl font-bold mb-2">
            {{ marker.title }}
          </h2>
          <div
            class="text-base text-muted-foreground"
            v-html="marker.description"
          />
        </LPopup>
      </LMarker>
    </LMap>
  </div>
</template>

<style scoped>
:deep(.leaflet-popup-content-wrapper) {
  max-width: v-bind('typeof popupWidth === "number" ? `${popupWidth}px` : popupWidth');
}

:deep(.leaflet-popup-content) {
  width: v-bind('typeof popupWidth === "number" ? `${popupWidth}px` : popupWidth');
  margin: 13px 19px;
}
</style>
